import {monthLabel, parseMonthKey} from '@vat/engine/month';
import {VAT_BETA_COPY, VAT_OBJEKT_COPY, VAT_TAX_STATUS_LABELS} from '@vat/vat-copy';
import type {VatExcelColumn, VatExcelSheet} from './vat-excel';
import {formatDateDE, formatDateTimeDE, steuernummerLabel} from './vat-format';
import type {ObjektAbrechnungInput, ObjektAbrechnungModel, ObjektKontoRow, ObjektRateGroup} from './vat-objekt-abrechnung';
import {objektBesteuerungLabel, objektPeriodLabel, objektSaldoKind} from './vat-objekt-pdf';

/**
 * Excel-Blattmodell der „Umsatzsteuerabrechnung je Objekt“ (Spec 2026-09-13 Sektion 2.3, Task C1) für den bestehenden
 * `writeVatExcelWorkbook`. Datumszellen tragen `kind: 'date'` (Writer wandelt über `toExcelDate`).
 *
 * Entscheidung: Die Spec nennt fünf Blätter; die Einheitenliste zum Stichtag (PDF Seite 2) hätte in keinem davon
 * passende Spalten und ginge im Excel verloren – deshalb ein sechstes Blatt „Einheiten“.
 *
 * @author Anthony Nahas
 * @since 13.09.26
 */
const S = VAT_OBJEKT_COPY.excel.sheets;
export const VAT_OBJEKT_EXCEL_SHEET_NAMES = [S.abrechnung, S.ertraege, S.kosten, S.nichtSteuerbar, S.flaechen, S.einheiten] as const;

const T = VAT_OBJEKT_COPY.table;
const C = VAT_OBJEKT_COPY.excel.columns;

const KONTO_COLUMNS: VatExcelColumn[] = [
    {header: C.satz, key: 'satz', width: 9, kind: 'int'},
    {header: T.konto, key: 'konto', width: 14, kind: 'text'},
    {header: T.bezeichnung, key: 'bezeichnung', width: 40, kind: 'text'},
    {header: T.brutto, key: 'brutto', width: 14, kind: 'money'},
    {header: T.netto, key: 'netto', width: 14, kind: 'money'},
    {header: T.ust, key: 'ust', width: 14, kind: 'money'},
    {header: T.anrechenbar, key: 'anrechenbar', width: 26, kind: 'text'},
    {header: T.anrechenbareUst, key: 'anrechenbareUst', width: 18, kind: 'money'},
    {header: C.zeilen, key: 'zeilen', width: 8, kind: 'int'},
];

const kontoRow = (group: ObjektRateGroup, row: ObjektKontoRow): Record<string, unknown> => ({
    satz: group.rate,
    konto: row.konto,
    bezeichnung: row.label,
    brutto: row.brutto,
    netto: row.netto,
    ust: row.ust,
    anrechenbar: row.anrechenbarLabel,
    anrechenbareUst: row.anrechenbareUst,
    zeilen: row.lineCount,
});

const kontoSheet = (name: string, groups: ObjektRateGroup[]): VatExcelSheet => ({
    name,
    titleRows: [],
    columns: KONTO_COLUMNS,
    rows: groups.flatMap(group => group.rows.map(row => kontoRow(group, row))),
});

const headerTitleRows = (input: ObjektAbrechnungInput): string[][] => {
    const h = VAT_OBJEKT_COPY.header;
    const periodText = objektPeriodLabel(input.period);
    return [
        [VAT_OBJEKT_COPY.title, periodText],
        [h.objekt, input.property.label],
        [h.eigentuemer, [input.landlord.name, input.landlord.addressLine].filter(Boolean).join(', ')],
        [h.einordnung, VAT_TAX_STATUS_LABELS[input.taxProfile.status]],
        [h.besteuerung, objektBesteuerungLabel(input)],
        [h.steuernummer, steuernummerLabel(input.taxProfile)],
        [h.zeitraum, `${periodText} (${h.zeitraumRange(formatDateDE(input.period.start), formatDateDE(input.period.end))})`],
        [C.engine, input.engineVersion],
        [C.erstelltAm, formatDateTimeDE(input.now)],
        [C.hinweis, `${VAT_BETA_COPY.footer}. ${VAT_OBJEKT_COPY.subtitle}.`],
    ];
};

const abrechnungSheet = (model: ObjektAbrechnungModel, input: ObjektAbrechnungInput): VatExcelSheet => {
    const e = VAT_OBJEKT_COPY.ergebnis;
    return {
        name: S.abrechnung,
        titleRows: headerTitleRows(input),
        columns: [
            {header: C.position, key: 'position', width: 52, kind: 'text'},
            {header: C.betrag, key: 'betrag', width: 18, kind: 'money'},
        ],
        rows: [
            {position: e.ertraegeUst, betrag: model.totals.ertraegeUst},
            {position: e.kostenAnrechenbar, betrag: model.totals.kostenAnrechenbar},
            {position: `${e.saldo} – ${objektSaldoKind(model.totals.saldo)}`, betrag: model.totals.saldo},
        ],
    };
};

const nichtSteuerbarSheet = (model: ObjektAbrechnungModel): VatExcelSheet => ({
    name: S.nichtSteuerbar,
    titleRows: [],
    columns: [
        {header: `${T.bezeichnung} / ${T.grund}`, key: 'bezeichnung', width: 60, kind: 'text'},
        {header: T.brutto, key: 'brutto', width: 14, kind: 'money'},
    ],
    rows: model.nichtSteuerbar.map(row => ({bezeichnung: row.label, brutto: row.brutto})),
});

const monthBounds = (monthKey: string): { start: Date | null; end: Date | null } => {
    const parts = parseMonthKey(monthKey);
    return parts ? {start: new Date(parts.year, parts.month - 1, 1), end: new Date(parts.year, parts.month, 0)} : {start: null, end: null};
};

const flaechenSheet = (input: ObjektAbrechnungInput): VatExcelSheet => ({
    name: S.flaechen,
    titleRows: [],
    columns: [
        {header: T.monat, key: 'monat', width: 16, kind: 'text'},
        {header: C.monatsbeginn, key: 'monatsbeginn', width: 14, kind: 'date'},
        {header: C.monatsende, key: 'monatsende', width: 14, kind: 'date'},
        {header: T.gesamtflaeche, key: 'gesamtflaeche', width: 18, kind: 'int'},
        {header: T.steuerpflichtigeFlaeche, key: 'steuerpflichtigeFlaeche', width: 20, kind: 'int'},
        {header: T.anteil, key: 'anteil', width: 10, kind: 'percent'},
        {header: T.modus, key: 'modus', width: 30, kind: 'text'},
    ],
    rows: input.areaMonths.map(m => {
        const bounds = monthBounds(m.monthKey);
        return {
            monat: monthLabel(m.monthKey),
            monatsbeginn: bounds.start,
            monatsende: bounds.end,
            gesamtflaeche: m.totalArea,
            steuerpflichtigeFlaeche: m.taxableArea,
            anteil: m.share,
            modus: VAT_OBJEKT_COPY.areaModes[m.mode] ?? m.mode,
        };
    }),
});

const stichtagLabel = (input: ObjektAbrechnungInput): string => {
    const last = input.areaMonths[input.areaMonths.length - 1];
    if (last) {
        return monthLabel(last.monthKey);
    }
    const end = input.period.end;
    return monthLabel(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`);
};

const einheitenSheet = (input: ObjektAbrechnungInput): VatExcelSheet => ({
    name: S.einheiten,
    titleRows: [[VAT_OBJEKT_COPY.sections.einheiten(stichtagLabel(input)), input.property.label]],
    columns: [
        {header: T.art, key: 'art', width: 14, kind: 'text'},
        {header: T.bezeichnung, key: 'bezeichnung', width: 28, kind: 'text'},
        {header: T.nutzer, key: 'nutzer', width: 30, kind: 'text'},
        {header: T.flaeche, key: 'flaeche', width: 10, kind: 'int'},
        {header: T.steuerpflichtig, key: 'steuerpflichtig', width: 14, kind: 'text'},
        {header: T.leerstand, key: 'leerstand', width: 10, kind: 'text'},
        {header: C.einheitID, key: 'einheitID', width: 24, kind: 'text'},
    ],
    rows: input.units.map(u => ({
        art: u.categoryType ? VAT_OBJEKT_COPY.unitTypes[u.categoryType] ?? u.categoryType : T.leer,
        bezeichnung: u.label,
        nutzer: u.tenantsName ?? T.leer,
        flaeche: u.area,
        steuerpflichtig: u.taxable ? T.ja : T.nein,
        leerstand: u.vacant ? T.ja : T.nein,
        einheitID: u.unitID,
    })),
});

/** Reines Blatt-Modell in der Reihenfolge VAT_OBJEKT_EXCEL_SHEET_NAMES (Blätter auch leer). */
export const buildObjektExcelModel = (model: ObjektAbrechnungModel, input: ObjektAbrechnungInput): VatExcelSheet[] => [
    abrechnungSheet(model, input),
    kontoSheet(S.ertraege, model.ertraege),
    kontoSheet(S.kosten, model.kosten),
    nichtSteuerbarSheet(model),
    flaechenSheet(input),
    einheitenSheet(input),
];
